/**
 * WhatsNewModal Component
 * 
 * Main modal component for displaying new features to users.
 * Uses Material-UI Dialog with responsive design and accessibility support.
 * 
 * Features:
 * - Smooth grow and fade-in animations for dialog appearance
 * - Staggered entrance animations for feature items
 * - Respects prefers-reduced-motion for accessibility
 * - Semantic Dialog with proper ARIA attributes (labelledby, describedby)
 * - Keyboard navigation: Escape to close, Tab for focus management
 * - Screen reader support with proper heading hierarchy
 * - Focus trap within dialog (built-in with MUI Dialog)
 * - Proper button labeling and roles
 * - Color contrast compliance
 * - Semantic HTML structure
 */

import React, { useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Stack,
  Grow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FeatureConfig } from '../types/featureConfig';
import FeatureItem from './FeatureItem';
import { modalStyles } from '../utils/modalStyles';


interface WhatsNewModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Feature configuration containing release info and features */
  featureConfig: FeatureConfig;
}

/**
 * WhatsNewModal component - displays feature release information
 * 
 * @param open - Controls modal visibility
 * @param onClose - Callback fired when user closes the modal (via close button, escape key, or Got It button)
 * @param featureConfig - Feature configuration with title, version, release date, and features array
 */
const WhatsNewModal: React.FC<WhatsNewModalProps> = ({
  open,
  onClose,
  featureConfig,
}) => {
  // Get theme and media query hook for responsive behavior
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State for screen reader announcements
  const announcementRef = useRef<HTMLDivElement>(null);
  
  // Announce modal state to screen readers
  useEffect(() => {
    if (announcementRef.current) {
      announcementRef.current.textContent = open 
        ? `What's New modal opened. ${featureConfig.title} - Version ${featureConfig.version}` 
        : 'What\'s New modal closed';
    }
  }, [open, featureConfig.title, featureConfig.version]);
  
  // Handle keyboard and accessibility events
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      {/* Screen reader announcement region */}
      <Box
        ref={announcementRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
      <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="whats-new-title"
      aria-describedby="whats-new-description"
      // MUI Dialog automatically handles:
      // - Focus trap (Tab navigation stays within dialog)
      // - Escape key closing
      // - Backdrop click closing (if disableEscapeKeyDown is not set)
      role="alertdialog"
      TransitionComponent={Grow}
      transitionDuration={{ enter: 300, exit: 200 }}
      sx={{
        ...((isMobile ? { ...modalStyles.dialog, ...modalStyles.mobileSheet } : modalStyles.dialog) as any),
        zIndex: (theme) => theme.zIndex.modal + 1,
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: '8px', sm: '12px' },
          maxHeight: '90vh',
          margin: { xs: 1, sm: 'auto' },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          // Enhanced animation for paper
          animation: 'modalPaperEnter 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes modalPaperEnter': {
            from: {
              opacity: 0,
              transform: 'scale(0.95)',
            },
            to: {
              opacity: 1,
              transform: 'scale(1)',
            },
          },
        },
      }}
      BackdropProps={{
        sx: {
          animation: 'backdropFadeIn 300ms ease-in-out',
          '@keyframes backdropFadeIn': {
            from: {
              opacity: 0,
            },
            to: {
              opacity: 1,
            },
          },
        },
      }}
    >
      <DialogTitle
        id="whats-new-title"
        sx={modalStyles.dialogTitle}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 600,
              display: 'block',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              wordBreak: 'break-word',
              margin: 0,
            }}
          >
            {featureConfig.title}
          </Typography>
          <Typography
            variant="caption"
            component="time"
            display="block"
            dateTime={featureConfig.releaseDate}
            sx={{
              color: 'text.secondary',
              mt: 0.5,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
            }}
          >
            Version {featureConfig.version} • Released{' '}
            {new Date(featureConfig.releaseDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close dialog"
          onClick={handleClose}
          sx={{
            color: 'inherit',
            transition: 'background-color 0.2s',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        id="whats-new-description"
        dividers
        sx={modalStyles.dialogContent}
      >
        <Stack spacing={2}>
          {featureConfig.features.map((feature, index) => (
            <FeatureItem key={feature.id} feature={feature} index={index} />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={modalStyles.dialogActions}
      >
        <Button
          onClick={handleClose}
          color="primary"
          variant="contained"
          aria-label="Close What's New dialog"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            py: 1,
            px: 3,
            minWidth: '100px',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
        >
          Got It
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default WhatsNewModal;
